import clsx from 'clsx';
import PropTypes from 'prop-types';
import {useFormContext} from 'react-hook-form';

import logger from 'conf/Logger';
import useUniqueId from 'hooks/uniqId';
import {checkErrors, renderLabel} from 'utils/formUtils';

import './Group.scss';


const propTypes = {
  id: PropTypes.string,
  label: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  help: PropTypes.string,
  helpLink: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
  render: PropTypes.func.isRequired,
};
/**
 * Renders a form group wrapper around a form control via render prop. The form group handles the label, help text, and
 * displaying validation messages below the control.
 *
 * @param props {object} - props container
 * @param [props.id] {string} - a unique id to apply to the form control and label, optional since it can be auto-generated
 * @param props.name {string} - the name of the prop this group will control in the form data
 * @param props.label {string} - a text label for the control, required
 * @param props.render {Function} - a render function for the form control component, first arg is the id of the form group and the second arg is a boolean value for whether the control is invalid (true) or valid (false)
 * @param [props.readOnly = false] {boolean} - marks the control as read-only and will submit the value as part of form data in form submission
 * @param [props.disabled = false] {boolean} - marks the control as disabled and will NOT submit the value as part of form data in form submission
 * @param [props.help] {string} - help text to show beneath the control
 * @param [props.helpLink] {string} - url, will be used as an "info" icon link next tot the group label
 */
export default function FormGroup({id: origId, label, name, help, helpLink,
  readOnly = false, disabled = false, className, style, render}) {
  const id = useUniqueId({id: origId, prefix: `${name}-`});
  const {formState: {errors}} = useFormContext();

  if (readOnly && disabled) {
    logger.error('Select cannot be both readOnly and disabled');
    return <div className="text-danger">error - check console</div>;
  }

  const {invalid, errorMsg} = checkErrors({disabled, errors, name});
  return (
    <div
      className={clsx('form-group', className, {'is-invalid': invalid, 'text-muted': disabled || readOnly})}
      style={style}
    >
      <label htmlFor={id} className={clsx({'text-danger': invalid})}>{renderLabel(label, helpLink)}</label>
      {render(id, invalid)}
      {help && <small className="form-text text-muted">{help}</small>}
      {errorMsg}
    </div>
  );
}
FormGroup.propTypes = propTypes;
